import { _ } from "../../../polyglot/polyglot.js";
import { addMonths } from "../../../helpers/date-util.js";
import {
	makeImportApiRepository,
	makeMonitorRepository,
} from "../../../repositories/repositoryBuilders.js";
import { router } from "@foretagsplatsen/widgetjs";
import AbstractImporter from "../AbstractImporter.js";
import CapegoIntegration from "../../../../models/integration/CapegoIntegration.js";
import session from "../../../session.js";
import SieFiles from "./SieFiles.js";
import SieFileSelectionStep from "./SieFileSelectionStep.js";

const numberOfYearsToFetchFiscalYearsFrom = 7;
const numberOfMonthsPerYear = 12;

const numberOfMonthsToFetchFiscalYearsFrom =
	numberOfYearsToFetchFiscalYearsFrom * numberOfMonthsPerYear;

/**
 * `CapegoImporter` Import wizard for Capego integration.
 *
 * @abstract
 */
export default class CapegoImporter extends AbstractImporter {
	constructor() {
		super(...arguments);

		this._capegoImportRepository = makeMonitorRepository({
			route: "/Shared/Import/Capego",
			antiForgeryToken: session.getAntiForgeryToken(),
		});

		this._router = router.getRouter();

		this._importApiRepository = makeImportApiRepository({
			route: "/Countries/#countryId/Agencies/#agencyId/Companies/#companyId/SieFiles",
			model: SieFiles,
			defaultFilters: {
				countryId: session.getCountryReferentialApiId(),
			},
		});
	}

	_wizardSteps() {
		return [this._selectFiscalYearStep()];
	}

	_selectFiscalYearStep() {
		return new SieFileSelectionStep({
			submitCallback: this._postData.bind(this),
		});
	}

	async _doStart({ onSuccess, onError }) {
		this._context.sieFiles = await this._fetchSieFiles();

		if (this._context.sieFiles.isEmpty()) {
			onError({ errorMessage: _("No SIE files found for this company") });
			return;
		}

		onSuccess();
	}

	async _fetchSieFiles() {
		const today = new Date();

		const sevenYearsAgo = addMonths(
			today,
			-numberOfMonthsToFetchFiscalYearsFrom,
		);

		return await this._importApiRepository.get({
			filter: {
				agencyId: this.getReferentialApiAgencyId(),
				companyId: this.getReferentialApiCompanyId(),
				lastModified: sevenYearsAgo.toISOString(),
			},
		});
	}

	async _postData({ onDone, step }) {
		const sieFile = this._context.selectedSieFile;

		if (!sieFile) {
			step.addSubmitError(
				_("No SIE file found for the selected fiscal year."),
			);

			onDone();
			return;
		}

		try {
			const data = await this._capegoImportRepository.save({
				filter: {
					agencyId: this.getAgencyId(),
					sieFileId: sieFile.getId(),
					companyId: this.getCompanyId(),
				},
				notify: false,
			});

			if (data.hasErrors) {
				step.addSubmitError(
					this._capegoImportErrorsToMessage(data.errors),
				);

				return;
			}
			this._context.importData = data;
		} catch (error) {
			step.addSubmitError(this._errorToMessage(error));
		} finally {
			onDone();
		}
	}

	_errorToMessage(error) {
		return error.responseJSON.message;
	}

	_capegoImportErrorsToMessage(errors) {
		return errors.map((error) => error.explanation).join(",");
	}
}

CapegoImporter.integrationType = CapegoIntegration.getType();
